import { makeStyles, shorthands, tokens, Button, Divider, Text, mergeClasses, Label } from '@fluentui/react-components';
import { ArrowLeft24Regular, Settings24Regular, QuestionCircle24Regular } from '@fluentui/react-icons';
import { FC, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { SettingsMenuContext } from '../../Contexts/SettingsMenuContext';
import { useFeature } from '../../store/hooks';
import { useIsMobile } from '../../Hooks/useResponsive';
import MenuBottom from './MenuBottom';
import MenuBottomZone from './MenuBottomZone';
import { 
    SettingsConversationsRoute,
    SettingsUserManagementRoute,
    SettingsSurveysRoute
} from '../../Routes';

const useStyles = makeStyles({
    root: {
        display: 'grid',
        gridTemplateRows: '1fr',
        height: '100%',
        boxSizing: 'border-box',
        backgroundColor: tokens.colorNeutralBackground1,
        width: '100%',
        position: 'relative',
        ...shorthands.overflow('hidden'),
    },
    rootExpanded: {
        padding: '20px 20px 0 18px', // top, right, bottom, left
    },
    rootCollapsed: {
        ...shorthands.padding(tokens.spacingVerticalM, '0'),
        placeItems: 'center',
    },
    content: {
        display: 'grid',
        gridTemplateRows: '1fr',
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        minHeight: 0,
        gridRow: '1',
    },
    topContent: {
        display: 'grid',
        gridTemplateRows: 'auto',
        gridAutoRows: 'auto',
        rowGap: '16px',
        overflow: 'hidden',
        minHeight: 0,
        alignContent: 'start',
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        ...shorthands.gap('8px'),
        cursor: 'pointer',
        '&:hover': {
            opacity: 0.8,
        },
    },
    headerIcon: {
        fontSize: '24px',
        color: tokens.colorNeutralForeground1,
    },
    headerText: {
        fontSize: '16px',
        fontWeight: 700,
        lineHeight: '24px',
        color: tokens.colorNeutralForeground1,
    },
    menuItem: {
        width: '100%',
        justifyContent: 'flex-start',
        minHeight: '36px',
        ...shorthands.padding('8px'),
        fontSize: '14px',
        fontWeight: 400,
        color: tokens.colorNeutralForeground1,
        backgroundColor: 'transparent',
        ...shorthands.border('none'),
        '&:hover': {
            backgroundColor: 'var(--menu-button-hover-bg)',
            color: 'var(--color-gray-800)',
            borderRadius: '0.5rem',
        },
    },
    menuItemActive: {
        backgroundColor: 'var(--menu-button-hover-bg)',
        borderRadius: '0.5rem',
        fontWeight: 500,
    },
    menuItemWithIcon: {
        ...shorthands.gap('4px'),
    },
    divider: {
        height: '1px',
        width: '100%',
        backgroundColor: tokens.colorNeutralStroke2,
    },
    iconButton: {
        width: '38px',
        height: '38px',
        minWidth: '38px',
        ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
        ...shorthands.borderRadius('8px'),
    },
    menuSection: {
        display: 'grid',
        gridTemplateColumns: '1fr',
        rowGap: '2px',
        marginLeft: '7px',
    },
});

interface SettingsMenuProps {
    collapsed?: boolean;
}

const SettingsMenu: FC<SettingsMenuProps> = ({ collapsed = false }) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { setIsInSettings, activeTab, previousRoute, setPreviousRoute } = useContext(SettingsMenuContext);
    const chatFeature = useFeature('chat');
    const isAdmin = chatFeature.isAdmin === true;
    const isMobile = useIsMobile();

    const handleBackToMain = () => {
        setIsInSettings(false);
        // Navigate back to the previous route, or default to /chat
        const targetRoute = previousRoute || '/chat';
        setPreviousRoute(null);
        navigate(targetRoute);
    };

    return (
        <div className={mergeClasses(classes.root, collapsed ? classes.rootCollapsed : classes.rootExpanded)}>
            {!collapsed && (
                <div className={classes.content}>
                    <div className={classes.topContent}>
                        {/* Header with Back Arrow */}
                        <div className={classes.header} onClick={handleBackToMain}>
                            <ArrowLeft24Regular className={classes.headerIcon} />
                            <Text className={classes.headerText}>{t('menu.settings.settings', 'Settings')}</Text>
                        </div>

                        {/* USER Section */}
                        <Label className="menuSectionLabel">{t('menu.settings.user', 'USER')}</Label>
                        <div className={classes.menuSection}>
                            <Button
                                appearance="transparent"
                                className={mergeClasses(classes.menuItem, activeTab === 'conversations' && classes.menuItemActive)}
                                onClick={() => navigate(SettingsConversationsRoute)}
                            >
                                {t('menu.settings.chats', 'Chats ...')}
                            </Button>
                        </div>


                        {/* ADMIN Section - only visible to admins */}
                        {isAdmin && (
                            <>
                                {/* Divider */}
                                <div className={classes.divider} />

                                <Label className="menuSectionLabel">{t('menu.settings.admin', 'ADMIN')}</Label>
                                <div className={classes.menuSection}>
                                    <Button
                                        appearance="transparent"
                                        className={mergeClasses(classes.menuItem, activeTab === 'user-management' && classes.menuItemActive)}
                                        onClick={() => navigate(SettingsUserManagementRoute)}
                                    >
                                        {t('menu.settings.userManagement', 'User management')}
                                    </Button>
                                    <Button
                                        appearance="transparent"
                                        className={mergeClasses(classes.menuItem, activeTab === 'surveys' && classes.menuItemActive)}
                                        onClick={() => navigate(SettingsSurveysRoute)}
                                    >
                                        {t('menu.settings.surveys', 'Surveys')}
                                    </Button>
                                </div>
                            </>
                        )}
                    </div>

                    {/* Bottom Icon Buttons */}
                    <div style={{ width: '100%' }}>
                        <MenuBottomZone />
                    </div>
                </div>
            )}
        </div>
    );
};

export default SettingsMenu;
